import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { formatDistanceToNow } from "date-fns";
import { AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { AdminGuard } from "@/components/role-guard";
import { TableSkeleton } from "@/components/skeletons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { trpc, withErrorToast } from "@/utils/trpc";
import type { AppRouter } from "../../../server/dist/routers";

type HealthData = inferRouterOutputs<AppRouter>["admin"]["systemHealth"];

function relative(value: string | Date | null) {
  if (!value) {
    return "Never";
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }
  return formatDistanceToNow(date, { addSuffix: true });
}

export default function AdminSystemHealth() {
  const health = useQuery(
    withErrorToast(trpc.admin.systemHealth.queryOptions())
  );

  return (
    <AdminGuard>
      <PageContainer className="flex flex-col gap-6">
        <PageHeader
          title="System health"
          description="Database, event-log throughput, and projection lag for the event-sourced backend."
          toolbar={
            <Button
              variant="light"
              size="sm"
              onClick={() => health.refetch()}
              disabled={health.isFetching}
            >
              <RefreshCw
                className={cn("h-4 w-4", health.isFetching && "animate-spin")}
              />
              {health.isFetching ? "Refreshing…" : "Refresh"}
            </Button>
          }
        />

        {health.isLoading ? (
          <TableSkeleton columns={4} rows={6} />
        ) : health.isError || !health.data ? (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            Failed to load system health.
          </div>
        ) : (
          <HealthBody data={health.data} />
        )}
      </PageContainer>
    </AdminGuard>
  );
}

function HealthBody({ data }: { data: HealthData }) {
  const dbOk = data.database.status === "ok";

  return (
    <div className="flex flex-col gap-6">
      <div
        className={cn(
          "flex items-center gap-3 rounded-lg border p-4",
          dbOk
            ? "border-emerald-300/60 bg-emerald-50/60 text-emerald-900"
            : "border-destructive/40 bg-destructive/10 text-destructive"
        )}
      >
        {dbOk ? (
          <CheckCircle2 className="h-5 w-5 shrink-0" />
        ) : (
          <AlertTriangle className="h-5 w-5 shrink-0" />
        )}
        <div>
          <div className="text-sm font-semibold">
            {dbOk ? "Database reachable" : "Database unreachable"}
          </div>
          <div className="text-xs opacity-80">
            {dbOk
              ? `Checked ${relative(data.database.timestamp)}`
              : (data.database.error ?? "Unknown error")}
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Event-log head" value={data.eventLog.headSeq} />
        <MetricCard label="Total events" value={data.eventLog.totalEvents} />
        <MetricCard label="Events (24h)" value={data.eventLog.eventsLast24h} />
        <MetricCard
          label="Max projection lag"
          value={data.maxLag}
          tone={data.maxLag === 0 ? "good" : data.maxLag > 100 ? "bad" : "warn"}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Projection lag</CardTitle>
          <CardDescription>
            Per-projector catch-up cursor versus the event-log head. A growing
            gap means a read model is falling behind.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="table-panel border-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="px-4">Projector</TableHead>
                  <TableHead className="px-4 text-right">Last seq</TableHead>
                  <TableHead className="px-4 text-right">Lag</TableHead>
                  <TableHead className="px-4 text-right">Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.projections.map((projection) => (
                  <TableRow key={projection.name}>
                    <TableCell className="px-4 py-3 text-sm font-medium text-foreground">
                      {projection.name}
                      {!projection.hasCheckpoint ? (
                        <Badge variant="outline" className="ml-2">
                          No catch-up yet
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right text-sm text-muted-foreground tabular-nums">
                      {projection.lastSeq.toLocaleString()}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right">
                      <Badge variant={lagVariant(projection.lag)}>
                        {projection.lag.toLocaleString()}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right text-sm text-muted-foreground">
                      {relative(projection.updatedAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <BreakdownCard
          title="Events by aggregate"
          description="Where events originate in the domain model."
          rows={data.eventLog.byAggregate}
        />
        <BreakdownCard
          title="Events by source"
          description="Channel that produced each event."
          rows={data.eventLog.bySource}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">GDPR key store</CardTitle>
          </CardHeader>
          <CardContent className="flex gap-6">
            <Stat label="Active" value={data.gdprKeys.active} />
            <Stat label="Erased" value={data.gdprKeys.erased} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Unresolved email</CardTitle>
          </CardHeader>
          <CardContent className="flex items-end justify-between">
            <Stat
              label="Awaiting triage"
              value={data.unresolvedInboundEmails}
              tone={data.unresolvedInboundEmails > 0 ? "warn" : "good"}
            />
            {data.unresolvedInboundEmails > 0 ? (
              <Button asChild variant="light" size="sm">
                <Link to="/admin/unresolved-email">Review</Link>
              </Button>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">D1 backups</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <div className="text-sm text-foreground">
              Last backup {relative(data.backups.lastBackupAt)}
            </div>
            <div className="text-xs text-muted-foreground">
              {data.backups.count.toLocaleString()} stored
            </div>
          </CardContent>
        </Card>
      </div>

      <p className="text-xs text-muted-foreground">
        Generated {relative(data.generatedAt)}
      </p>
    </div>
  );
}

function lagVariant(lag: number) {
  if (lag === 0) {
    return "secondary" as const;
  }
  if (lag > 100) {
    return "destructive" as const;
  }
  return "outline" as const;
}

function MetricCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "good" | "warn" | "bad";
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div
          className={cn(
            "mt-1 text-3xl font-semibold tabular-nums",
            tone === "bad" && "text-destructive",
            tone === "warn" && "text-amber-600",
            (!tone || tone === "good") && "text-foreground"
          )}
        >
          {value.toLocaleString()}
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "good" | "warn";
}) {
  return (
    <div>
      <div
        className={cn(
          "text-2xl font-semibold tabular-nums",
          tone === "warn" ? "text-amber-600" : "text-foreground"
        )}
      >
        {value.toLocaleString()}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function BreakdownCard({
  title,
  description,
  rows,
}: {
  title: string;
  description: string;
  rows: { key: string; count: number }[];
}) {
  const total = rows.reduce((sum, r) => sum + r.count, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No events yet.</p>
        ) : (
          rows.map((row) => {
            const pct = total > 0 ? Math.round((row.count / total) * 100) : 0;
            return (
              <div key={row.key} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-foreground">{row.key}</span>
                  <span className="text-muted-foreground tabular-nums">
                    {row.count.toLocaleString()} ({pct}%)
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-foreground/70"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
